import { useMemo, useState } from 'react';
import { UseQueryOptions, useQuery, useQueryClient } from 'react-query';
import { ProjectId } from 'api/models';
import {
  Project,
  ProjectAPI,
  ProjectCreationStatus,
  ProjectCreationStatusState,
} from 'api/project';
import { ProjectInUserProfile } from 'api/user';
import { useProjectsFromInitData } from 'queries/account';
import {
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { ApiErrorData } from 'queries/error';
import { useProjectDetailQuery } from 'queries/project';

export const useProjectDetail = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
) => {
  return useProjectDetailQuery(projectId, {
    ...options,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    notifyOnChangeProps: 'tracked',
    enabled: typeof projectId?.[1] === 'number',
  });
};

export const useProject = ([, id]: ProjectId):
  | Pick<ProjectInUserProfile, 'projectId' | 'projectName'>
  | undefined => {
  const { data } = useProjectsFromInitData();

  return useMemo(() => data?.byId[id], [data?.byId, id]);
};

export const projectCreateStatusQueryKey = (
  projectId: string | number,
): (string | number)[] => {
  return ['project-creation-status', projectId];
};

export const useProjectCreationStatus = (projectId: number) => {
  const [prevState, setPrevState] = useState<ProjectCreationStatus[]>([]);
  const queryClient = useQueryClient();

  const query = useQuery(
    projectCreateStatusQueryKey(projectId),
    () => {
      return ProjectAPI.getProjectCreationStatus(projectId);
    },
    {
      refetchInterval: (data) => {
        if (data) {
          const inprogress =
            data?.filter(
              (e) => e?.status === ProjectCreationStatusState.InProgress,
            ) ?? [];

          if (inprogress.length > 0) {
            return 1000;
          }

          return false;
        }

        return false;
      },
      onSuccess: async (data) => {
        if (data) {
          setPrevState(data);
        }
        const completedCount = data?.filter(
          (e) => e?.status === ProjectCreationStatusState.Completed,
        )?.length;

        const prevCompletedCount = prevState?.filter(
          (e) => e?.status === ProjectCreationStatusState.Completed,
        )?.length;

        if (prevCompletedCount < completedCount && completedCount !== 0) {
          await queryClient.invalidateQueries(
            getProjectTasksQueryKey(['project_id', projectId]),
          );
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(['project_id', projectId]),
          );
        }
      },
    },
  );

  return query;
};

export const useGetProjectMembers = (projectId: number) => {
  return useQuery(['project-members', projectId], () =>
    ProjectAPI.getProjectMembers(projectId),
  );
};
